import React from 'react';
import {
    UserIcon,
    MapPinIcon,
    CalendarIcon,
    DocumentTextIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';

/**
 * Componente que muestra el encabezado del panel de detalle de cobranza,
 * incluyendo información del perfil, detalles del crédito y estado de pagos.
 * 
 * @param {Object} props
 * @param {Object} props.data - Datos generales del crédito
 * @param {Object} props.item - Datos del cliente
 * @param {Object} props.direccionData - Datos de dirección
 * @param {Object} props.amortizacionStats - Estadísticas de amortización calculadas
 */
export const DashboardHeader = ({ data, item, direccionData, amortizacionStats, articulosFactura = [] }) => {
    // Derived values for the premium dashboard header
    const proyectado = data?.Valor_Cobrar_Proyectado || 0;
    
    // Si Valor_Cobrado_Total existe y es mayor que 0 lo usamos, sino usamos el abono de amortizacion, y por ultimo Valor_Cobrado
    let cobrado = data?.Valor_Cobrado_Total > 0 
        ? data.Valor_Cobrado_Total 
        : (amortizacionStats?.fetched && amortizacionStats?.totalAbono > 0 
            ? amortizacionStats.totalAbono 
            : (data?.Valor_Cobrado || 0));
    
    const avancePorcentaje = proyectado > 0 ? ((cobrado / proyectado) * 100).toFixed(0) + '%' : '0%';
    
    const cuotasPagadas = amortizacionStats?.fetched ? amortizacionStats.pagadas : (data?.CuotasPagadas || 0);
    const cuotasImpagas = amortizacionStats?.fetched ? amortizacionStats.impagas : (data?.CuotasVencidas || 0);
    const cuotasPendientes = amortizacionStats?.fetched ? amortizacionStats.pendientes : (data?.CuotasPendientes || 0);
    const totalCuotas = cuotasPagadas + cuotasImpagas + cuotasPendientes;
    const plazo = amortizacionStats?.fetched ? amortizacionStats.plazo : (data?.Plazo || totalCuotas || 'N/D');

    return (
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Card 1: Perfil y Progreso */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col justify-between transition-transform hover:scale-[1.02] duration-300">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg shadow-inner">
                                {data?.Cliente ? data.Cliente.charAt(0) : (item?.Cliente ? item.Cliente.charAt(0) : 'C')}
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 leading-tight uppercase line-clamp-1 text-sm" title={data?.Cliente || item?.Cliente || 'CLIENTE NO REGISTRADO'}>
                                    {data?.Cliente || item?.Cliente || 'CLIENTE NO REGISTRADO'}
                                </h3>
                                <p className="text-sm text-slate-500 font-medium flex items-center gap-1.5 mt-1">
                                    <UserIcon className="w-4 h-4" /> {data?.Cedula || item?.Cedula || 'SIN CÉDULA'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 text-[11px] text-slate-600 mt-2 bg-slate-50 p-1.5 rounded-lg">
                            <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="line-clamp-2 uppercase leading-relaxed">
                                {direccionData?.canton || direccionData?.CallePrincipal ? `${direccionData?.canton || ''} ${direccionData?.canton && direccionData?.CallePrincipal ? '-' : ''} ${direccionData?.CallePrincipal || ''}` : 'SIN DIRECCIÓN'}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex justify-between text-[11px] mb-1">
                            <span className="text-slate-500 font-medium">Avance de Cobro</span>
                            <span className="font-bold text-blue-600">{avancePorcentaje}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 mb-1.5 overflow-hidden shadow-inner">
                            <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-1.5 rounded-full" style={{ width: avancePorcentaje }}></div>
                        </div>
                        <div className="flex justify-between text-[10px] font-medium">
                            <span className="text-slate-500">Proyectado: <span className="text-slate-700">${proyectado.toFixed(2)}</span></span>
                            <span className="text-slate-500">Cobrado: <span className="text-green-600">${cobrado.toFixed(2)}</span></span>
                        </div>
                    </div>
                </div>

                {/* Card 2: Detalles del Crédito */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 transition-transform hover:scale-[1.02] duration-300">
                    <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="p-1 bg-blue-50 rounded-lg">
                            <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                        </div>
                        Detalles del Crédito
                    </h4>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-3 text-xs">
                        <div>
                            <p className="text-xs text-slate-400 mb-1 uppercase font-semibold">Documento</p>
                            <p className="font-bold text-slate-800 bg-slate-50 px-2 py-1 rounded w-max">{data?.Numero_Documento || 'N/D'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1 uppercase font-semibold">Producto</p>
                            {articulosFactura.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                    {articulosFactura.slice(0, 2).map((art, i) => (
                                        <span
                                            key={i}
                                            title={art.Articulo}
                                            className="inline-block text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-lg leading-tight truncate max-w-[160px]"
                                        >
                                            {art.Articulo?.length > 28
                                                ? art.Articulo.substring(0, 28) + '…'
                                                : art.Articulo}
                                        </span>
                                    ))}
                                    {articulosFactura.length > 2 && (
                                        <span className="text-[9px] text-slate-400 font-medium pl-1">
                                            +{articulosFactura.length - 2} más
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <p className="font-bold text-slate-800 uppercase">{data?.Cartera || 'N/D'}</p>
                            )}
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1 uppercase font-semibold">Fecha</p>
                            <p className="font-bold text-slate-800 flex items-center gap-1.5">
                                <CalendarIcon className="w-4 h-4 text-slate-400" />
                                {data?.Fecha_Factura ? new Date(data.Fecha_Factura).toLocaleDateString() : 'N/D'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 mb-1 uppercase font-semibold">Banco / Agencia</p>
                            <p className="font-bold text-slate-800 uppercase text-xs line-clamp-2 leading-tight">
                                {data?.Banco || 'N/D'} <br />
                                <span className="text-slate-500 font-medium">{data?.Almacen || 'N/D'}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Card 3: Estado de Pagos */}
                <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-4 shadow-sm border border-slate-100 relative overflow-hidden transition-transform hover:scale-[1.02] duration-300 group">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-red-50 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
                    <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center gap-2 relative z-10">
                        <div className="p-1 bg-red-50 rounded-lg">
                            <CurrencyDollarIcon className="w-4 h-4 text-red-500" />
                        </div>
                        Estado de Pagos
                    </h4>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs relative z-10">
                        <div>
                            <p className="text-[10px] text-slate-400 mb-0.5 uppercase font-semibold">Valor a Pagar</p>
                            <p className="font-black text-red-600 text-lg leading-none tracking-tight">
                                ${amortizacionStats?.fetched
                                    ? amortizacionStats.saldoImpagas.toFixed(2)
                                    : (data?.Cuota ?? proyectado.toFixed(2))}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 mb-0.5 uppercase font-semibold">Días de Mora</p>
                            <p className="font-black text-red-600 text-lg leading-none flex items-baseline gap-1">
                                {amortizacionStats?.fetched && amortizacionStats?.diasMora !== undefined ? amortizacionStats.diasMora : (data?.Dias_Mora_Proyectado || '0')} <span className="text-[10px] font-semibold text-red-400">días</span>
                            </p>
                        </div>
                        <div className="col-span-2 mt-1.5 bg-white p-2.5 rounded-xl border border-slate-100 shadow-sm">
                            <div className="flex justify-between text-[11px] text-slate-500 mb-1.5">
                                <span>Plazo: <strong>{plazo}</strong> cuotas</span>
                                <span className="font-bold text-slate-700">{cuotasPagadas} pagadas</span>
                            </div>
                            <div className="flex gap-1 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                {/* Green for pagadas, red for impagas, gray for pendientes */}
                                <div className="bg-emerald-500 h-full" style={{ flexGrow: cuotasPagadas }}></div>
                                <div className="bg-rose-500 h-full" style={{ flexGrow: cuotasImpagas }}></div>
                                <div className="bg-slate-300 h-full" style={{ flexGrow: cuotasPendientes }}></div>
                            </div>
                            <div className="flex justify-between text-[9px] mt-1.5 px-0.5 font-medium">
                                <div className="flex items-center gap-1 text-rose-500"><div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div> {cuotasImpagas} impagas</div>
                                <div className="flex items-center gap-1 text-slate-400"><div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div> {cuotasPendientes} pendientes</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
